import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  Ticket, Pause, Play, Trash2, Plus, ShieldAlert, ShieldCheck,
  Clock, AlertTriangle, Crown, Gift, ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * V13.14 — Admin Access Console for a specific learner.
 * Shows: ticket wallet, user suspension toggle, gift-ticket, audit log.
 *
 * Route: /admin/access/user/:userId
 */
export default function AdminAccessConsole() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [giftOpen, setGiftOpen] = useState(false);
  const [courses, setCourses] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [tr, ar, cr] = await Promise.all([
        api.get(`/api/admin/access/users/${userId}/tickets`),
        api.get(`/api/admin/access/users/${userId}/audit`),
        api.get('/api/courses'),
      ]);
      setData(tr.data);
      setAudit(ar.data.audit || []);
      setCourses(cr.data.courses || cr.data || []);
    } catch (e) {
      toast.error('Failed to load learner access');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [userId]);

  const toggleSuspend = async () => {
    const isSuspended = data?.user?.is_suspended;
    const endpoint = isSuspended ? 'unsuspend' : 'suspend';
    const reason = !isSuspended ? (prompt('Reason for suspension:') || 'Admin suspended') : '';
    if (!isSuspended && !reason) return;
    try {
      await api.post(`/api/admin/access/users/${userId}/${endpoint}`,
        isSuspended ? {} : { reason });
      toast.success(isSuspended ? 'Learner reactivated' : 'Learner suspended');
      load();
    } catch {
      toast.error('Failed');
    }
  };

  const suspendTicket = async (id) => {
    const reason = prompt('Reason:') || 'Admin pause';
    await api.post(`/api/admin/access/tickets/${id}/suspend`, { reason });
    toast.success('Ticket paused');
    load();
  };
  const reactivateTicket = async (id) => {
    await api.post(`/api/admin/access/tickets/${id}/reactivate`, {});
    toast.success('Ticket reactivated');
    load();
  };
  const revokeTicket = async (id) => {
    if (!confirm('Revoke this ticket permanently? (record is kept for audit)')) return;
    const reason = prompt('Reason:') || 'Admin revoke';
    await api.post(`/api/admin/access/tickets/${id}/revoke`, { reason });
    toast.success('Ticket revoked');
    load();
  };

  if (loading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!data?.user) return <div className="p-8">Learner not found.</div>;

  const u = data.user;
  const tickets = data.tickets || [];

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-5xl mx-auto" data-testid="admin-access-console">
      <Link to="/admin/users" className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground">
        <ArrowLeft className="h-3 w-3" />Back to users
      </Link>

      {/* Learner header */}
      <Card>
        <CardContent className="pt-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-heading font-semibold">{u.name || u.email}</h1>
            <p className="text-sm text-muted-foreground">{u.email}</p>
            <div className="flex gap-2 mt-3 flex-wrap">
              <Badge variant="secondary" className="text-[11px]">
                {u.learner_type || 'free'}
              </Badge>
              {u.is_suspended && (
                <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[11px]">
                  <ShieldAlert className="h-3 w-3 mr-1" />Account paused
                </Badge>
              )}
              {u.pregnancy_profile?.stage && (
                <Badge variant="outline" className="text-[11px]">
                  {u.pregnancy_profile.stage}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button
              variant={u.is_suspended ? 'default' : 'outline'}
              size="sm"
              onClick={toggleSuspend}
              className={u.is_suspended ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              data-testid="toggle-user-suspend-btn"
            >
              {u.is_suspended ? (
                <><ShieldCheck className="h-4 w-4 mr-1" />Reactivate account</>
              ) : (
                <><Pause className="h-4 w-4 mr-1" />Suspend account</>
              )}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setGiftOpen(true)} data-testid="gift-ticket-btn">
              <Gift className="h-4 w-4 mr-1" />Gift ticket
            </Button>
          </div>
        </CardContent>
        {u.is_suspended && u.suspended_reason && (
          <CardContent className="pt-0 pb-4">
            <div className="text-xs bg-rose-50 border border-rose-200 rounded p-2 text-rose-800">
              <strong>Reason:</strong> {u.suspended_reason}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Tickets */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-medium flex items-center gap-2">
            <Ticket className="h-4 w-4" />Tickets ({tickets.length})
          </h2>
        </div>
        {tickets.length === 0 ? (
          <Card className="p-6 text-center text-sm text-muted-foreground">
            No tickets yet. Gift one above or wait for an OPS purchase webhook.
          </Card>
        ) : (
          <div className="space-y-2">
            {tickets.map((t) => (
              <TicketRow
                key={t.id}
                t={t}
                onSuspend={() => suspendTicket(t.id)}
                onReactivate={() => reactivateTicket(t.id)}
                onRevoke={() => revokeTicket(t.id)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Audit log */}
      <section>
        <h2 className="text-base font-medium flex items-center gap-2 mb-3">
          <Clock className="h-4 w-4" />Audit log
        </h2>
        <Card>
          <CardContent className="p-0">
            <div className="max-h-72 overflow-y-auto divide-y">
              {audit.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">No audit events yet.</div>
              ) : audit.map((e, i) => (
                <div key={i} className="p-3 text-xs flex items-center justify-between">
                  <div>
                    <Badge variant="outline" className="text-[10px] mr-2">{e.action}</Badge>
                    {e.reason || '—'}
                  </div>
                  <span className="text-muted-foreground">
                    {new Date(e.timestamp).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      <GiftTicketDialog
        open={giftOpen}
        onClose={() => setGiftOpen(false)}
        userId={userId}
        courses={courses}
        onGranted={() => { setGiftOpen(false); load(); }}
      />
    </div>
  );
}


function TicketRow({ t, onSuspend, onReactivate, onRevoke }) {
  const isSuspended = t.status === 'suspended';
  const isRevoked = t.status === 'revoked';
  const cls = isRevoked
    ? 'opacity-50 bg-slate-50'
    : isSuspended
    ? 'bg-amber-50/40 border-amber-200'
    : '';

  return (
    <Card className={`p-3 flex items-center justify-between gap-3 ${cls}`}>
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="text-[10px]" variant={t.tier === 'full' ? 'default' : 'secondary'}>
            {t.tier}
          </Badge>
          <span className="text-sm font-medium">
            {t.target_type} · {t.target_id?.slice(-6)}
          </span>
          <Badge variant="outline" className="text-[10px]">
            {t.source}
          </Badge>
          {t.unlock_at_pregnancy_week && (
            <Badge variant="outline" className="text-[10px] text-rose-700 border-rose-300">
              Unlocks at week {t.unlock_at_pregnancy_week}
            </Badge>
          )}
          {t.status !== 'active' && (
            <Badge className="text-[10px] bg-slate-200 text-slate-700">{t.status}</Badge>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">
          Granted {new Date(t.granted_at).toLocaleDateString()} ·{' '}
          Expires {t.expires_at ? new Date(t.expires_at).toLocaleDateString() : 'Never'}
        </p>
      </div>
      {!isRevoked && (
        <div className="flex gap-1">
          {isSuspended ? (
            <Button size="sm" variant="ghost" onClick={onReactivate}>
              <Play className="h-3 w-3" />
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={onSuspend}>
              <Pause className="h-3 w-3" />
            </Button>
          )}
          <Button size="sm" variant="ghost" className="text-destructive" onClick={onRevoke}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      )}
    </Card>
  );
}


function GiftTicketDialog({ open, onClose, userId, courses, onGranted }) {
  const [courseId, setCourseId] = useState('');
  const [tier, setTier] = useState('full');
  const [unlockWeek, setUnlockWeek] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!courseId) return toast.error('Pick a course');
    setSaving(true);
    try {
      await api.post('/api/admin/access/grant', {
        user_id: userId,
        target_type: 'course',
        target_id: courseId,
        tier,
        unlock_at_pregnancy_week: unlockWeek ? parseInt(unlockWeek, 10) : null,
        reason: reason || 'Admin gift',
      });
      toast.success('Ticket gifted');
      onGranted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Grant failed');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="gift-ticket-dialog">
        <DialogHeader>
          <DialogTitle>Gift a Ticket</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <label className="text-xs font-medium">Course</label>
          <Select value={courseId} onValueChange={setCourseId}>
            <SelectTrigger data-testid="gift-course-select"><SelectValue placeholder="Pick course" /></SelectTrigger>
            <SelectContent>
              {courses.map((c) => (
                <SelectItem key={c._id || c.id} value={c._id || c.id}>
                  {c.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="text-xs font-medium">Tier</label>
          <Select value={tier} onValueChange={setTier}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="free_trial">Free trial</SelectItem>
              <SelectItem value="paid_trial">Paid trial</SelectItem>
              <SelectItem value="full">Full</SelectItem>
              <SelectItem value="gift">Gift (all access)</SelectItem>
            </SelectContent>
          </Select>
          <label className="text-xs font-medium">
            Unlock at pregnancy week (optional)
          </label>
          <Input
            type="number"
            placeholder="e.g. 32"
            value={unlockWeek}
            onChange={(e) => setUnlockWeek(e.target.value)}
            data-testid="gift-week-input"
          />
          <label className="text-xs font-medium">Reason (internal note)</label>
          <Input
            placeholder="Customer service, retention gift, etc."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="gift-submit-btn">
            {saving ? 'Granting…' : 'Grant ticket'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
